import { useState, useEffect, useRef } from "react";
import { X, CreditCard, Bank, ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function AdfinCheckout({ planId, cycle, packs = 0, planName, onClose }) {
  const [loading, setLoading] = useState(false);
  const [checkout, setCheckout] = useState(null); // { method, checkout_url, amount_gbp }
  const [done, setDone] = useState(false);
  const pollRef = useRef(null);

  const finish = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setDone(true);
    toast.success("Payment received — your subscription is now active!");
    setTimeout(() => onClose(), 1600);
  };

  const checkNow = async () => {
    try {
      const { data } = await api.get("/adfin/subscription-status");
      if (data?.active) finish();
      else toast.message("We haven't received your payment yet — it can take a few seconds. This window will close automatically once it clears.");
    } catch (e) {
      toast.message("Still confirming your payment — this window will close automatically once it clears.");
    }
  };

  // While the embedded checkout is open, poll the backend until Adfin confirms
  // the payment/mandate has cleared, then auto-close and return to this page.
  useEffect(() => {
    if (!checkout) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get("/adfin/subscription-status");
        if (data?.active) finish();
      } catch (e) { /* keep polling */ }
    }, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkout]);

  const start = async (method) => {
    setLoading(true);
    try {
      const { data } = await api.post("/adfin/subscribe", {
        plan_id: planId, billing_cycle: cycle, growth_packs: packs, method,
      });
      if (!data.ok || !data.checkout_url) {
        toast.error(data?.adfin_error?.errors?.[0]?.message || data?.detail || "Could not start checkout");
      } else {
        setCheckout(data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/60 backdrop-blur-sm p-4" data-testid="adfin-checkout-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <div className="font-heading font-bold text-navy">Set up your subscription</div>
            <div className="text-navy/70 text-xs">{planName || "Plan"} · {cycle === "annual" ? "Annual" : "Monthly"}</div>
          </div>
          <button onClick={onClose} data-testid="adfin-checkout-close" className="text-navy/65 hover:text-navy transition-colors"><X size={20} /></button>
        </div>

        {done ? (
          <div className="p-10 flex flex-col items-center justify-center text-center gap-3" data-testid="adfin-checkout-done">
            <CheckCircle size={56} weight="fill" className="text-positive" />
            <div className="font-heading font-bold text-navy text-lg">Payment received</div>
            <p className="text-navy/70 text-sm">Your subscription is now active. Returning to your subscription…</p>
          </div>
        ) : !checkout ? (
          <div className="p-6 space-y-3">
            <p className="text-navy/70 text-sm">Choose how you'd like to pay — everything happens right here, you won't leave the page.</p>
            <button disabled={loading} onClick={() => start("card")} data-testid="adfin-method-card"
              className="w-full flex items-center gap-3 p-4 rounded-xl border border-slate-200 hover:border-[#F37A24] transition-colors text-left disabled:opacity-50">
              <CreditCard size={26} weight="duotone" style={{ color: "#0E0F3F" }} />
              <div>
                <div className="font-semibold text-navy">Card</div>
                <div className="text-navy/70 text-xs">Pay by debit or credit card</div>
              </div>
            </button>
            <button disabled={loading} onClick={() => start("direct_debit")} data-testid="adfin-method-dd"
              className="w-full flex items-center gap-3 p-4 rounded-xl border border-slate-200 hover:border-[#F37A24] transition-colors text-left disabled:opacity-50">
              <Bank size={26} weight="duotone" style={{ color: "#0E0F3F" }} />
              <div>
                <div className="font-semibold text-navy">Direct Debit</div>
                <div className="text-navy/70 text-xs">Auto-collected each {cycle === "annual" ? "year" : "month"}</div>
              </div>
            </button>
            {loading && <div className="text-center text-navy/70 text-sm py-2" data-testid="adfin-checkout-loading">Preparing secure checkout…</div>}
          </div>
        ) : (
          <div className="flex-1 flex flex-col min-h-0">
            <div className="px-6 py-2 flex items-center gap-2 border-b border-slate-100">
              <button onClick={() => setCheckout(null)} data-testid="adfin-checkout-back"
                className="text-navy/70 hover:text-navy inline-flex items-center gap-1 text-xs transition-colors">
                <ArrowLeft size={14} /> Change method
              </button>
              <span className="text-navy/65 text-xs ml-auto">
                {checkout.method === "direct_debit" ? "Direct Debit mandate" : "Card payment"} · £{checkout.amount_gbp}
              </span>
            </div>
            <iframe src={checkout.checkout_url} title="Adfin checkout" data-testid="adfin-checkout-iframe"
              className="w-full flex-1 min-h-[520px] border-0"
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-top-navigation-by-user-activation" />
            <div className="px-6 py-3 flex items-center justify-between gap-3 border-t border-slate-100">
              <span className="text-navy/65 text-[0.7rem]">Secured by Adfin · Powered by Xero</span>
              <button onClick={checkNow} data-testid="adfin-checkout-done-btn"
                className="text-xs font-semibold text-[#C2410C] hover:underline">
                I've completed payment
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
